import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
    deleteKeyPersist,
    getKey,
    setKey,
    getKeyPersist,
    decryptShellValue,
} from "app/shared/utils/EncryptionUtility";
import {ISignUpForm} from "app/features/MyAccount/types/SignUpForm";
import {ChangePasswordForm} from "app/features/MyAccount/ChangePassword/types/ChangePasswordType";
import { ConfigResponse, SnackBarOptions } from "app/services/api/commonTypes";
import { MyAccountMenu, Routes, SNACKBAR_DURATION } from "app/shared/constants/constants";
import { RootState } from "../../store";

// TODO: Bypass to read the shell injected localstorage value using URI Key.
// Need cleanup after 11/11 release mobile should use the Body Encryption Key instead URI Key.
const getValuesForShellApp = (key:string) => {
    const platform = window?.platform?.toLowerCase();
    const isShellApp = platform === 'android' || platform === 'ios';
    if (isShellApp) {
        const val = window.localStorage.getItem(key);
        if (val) {
            return decryptShellValue(val);
        }
        return val;
    }
    return getKeyPersist(key);
};

const checkIsGuest = () => {
    //Check value exists
    const tempVal = getValuesForShellApp('isGuest');
    if (tempVal) {
        //if value exists is key persist is false then return false <- Is a loyalty user
        if (tempVal === 'false') {
            return false;
        }
    }
    return true;
};

export interface AuthState {
        disableRecaptcha: boolean;
        googleRecaptchaKey: string;
        user: null | string;
        token: null | string;
        modalOpen: boolean;
        cartOpen: boolean;
        upsellModalOpen: boolean;
        hasSeenUpsellModal: boolean;
        isGuest: boolean;
        showFooter: boolean;
        showMoreOptions: boolean;
        hideNavigation: boolean;
        isHeaderServiceFailure: boolean;
        forgotPasswordMsg: string;
        resetPasswordSuccessMsg: string;
        snackBarOptions: SnackBarOptions,
        signUpForm: ISignUpForm;
        isSignUpFormInvalid: boolean;
        welcomeModalOpen: boolean;
        preferencesModalOpen: boolean;
        configs: { env: Record<string, string>, brandConfigs: Record<string, string> };
        alreadySignUpErrorMsg: string | null;
        changePasswordForm: ChangePasswordForm;
        isChangePasswordFormInvalid: boolean;
        redirectToMyAccount: boolean;
        redirectToGiftCardTransfer: boolean;
        redirectToRewardsHome: boolean;
        redirectToAccountNotifications: boolean;
        showGlobalAlert: boolean;
        selectedMyAccountSelection: string;
        logoutRequested: boolean;
        forceLogoutErrorMsg: string | null;
    }
const initialState: AuthState = {
        googleRecaptchaKey: process.env.REACT_APP_RECAPTCHA_KEY ?? '',
        disableRecaptcha: process.env.REACT_APP_DISABLE_RECAPTCHA === "true",
        user: getValuesForShellApp('user') || null,
        token: null,
        modalOpen: false,
        cartOpen: false,
        upsellModalOpen: false,
        hasSeenUpsellModal: false,
        showFooter: true,
        isGuest: checkIsGuest(),
        showMoreOptions: false,
        //eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        hideNavigation: getKey('hideNavigation') ? JSON.parse(<string>getKey('hideNavigation')) : false,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        isHeaderServiceFailure: getKey('isHeaderServiceFailure') ? JSON.parse(<string>getKey('isHeaderServiceFailure')) : false,
        forgotPasswordMsg: '',
        resetPasswordSuccessMsg: '',
        snackBarOptions: {
            show: false,
            duration: SNACKBAR_DURATION,
            message: '',
            alertType: 'success',
        },
        signUpForm: {
            firstName: '',
            lastName: '',
            emailAddress: '',
            memberPhone: '',
            dob: '',
            password: '',
            confirmPassword: '',
            isSpecialOfferChecked: true,
            isCateringChecked: false,
            zipCode: '',
        },
        isSignUpFormInvalid: false,
        welcomeModalOpen: false,
        preferencesModalOpen: false,
        configs: { env: {}, brandConfigs: {} },
        alreadySignUpErrorMsg: null,
        changePasswordForm: {
            currentPassword: '',
            newPassword: '',
            confirmPassword: '',
        },
        isChangePasswordFormInvalid: false,
        redirectToMyAccount: false,
        redirectToGiftCardTransfer: false,
        redirectToRewardsHome: false,
        redirectToAccountNotifications: false,
        showGlobalAlert: true,
        selectedMyAccountSelection: window.location.pathname === Routes.MY_ACCOUNT
        ? MyAccountMenu.PERSONAL_INFO : MyAccountMenu.GIFT_CARD_TRANSFER,
        logoutRequested: false,
        forceLogoutErrorMsg: null,
    };
const authSlice = createSlice({
    name: "auth",
    initialState,
    reducers: {
        resetPasswordFields: (state) => {
            state.changePasswordForm = {
                currentPassword: '',
                newPassword: '',
                confirmPassword: '',
            };
            state.isChangePasswordFormInvalid = false;
        },
        reSetCredentials: (
            state,
        ) => {
            deleteKeyPersist('user');
            deleteKeyPersist('token');
            deleteKeyPersist('isGuest');
            state.user = null;
            state.token = null;
            state.isGuest = true;
        },
        setHideNavigation: (state, action: PayloadAction<boolean>) => {
            state.hideNavigation = action.payload;
            setKey("hideNavigation", JSON.stringify(action.payload));
        },
        setCredentials: (
            state,
            // eslint-disable-next-line max-len
            {payload: {user, token, isGuest}}: PayloadAction<{ user: string; token: string; isGuest: boolean }>,
        ) => {
            state.user = user;
            state.token = token;
            state.isGuest = isGuest;
        },
        toggleAuthModal: (state, action: PayloadAction<boolean>) => {
            state.modalOpen = action.payload;
        },
        toggleCartDrawer: (state, action: PayloadAction<boolean>) => {
            state.cartOpen = action.payload;
        },
        toggleUpsellModal: (state, action: PayloadAction<boolean>) => {
            state.upsellModalOpen = action.payload;
        },
        showMobileFooter: (state, action: PayloadAction<boolean>) => {
            state.showFooter = action.payload;
        },
        toggleMoreOptionsDrawer: (state, action: PayloadAction<boolean>) => {
            state.showMoreOptions = action.payload;
        },
        setHasSeenUpsellModal: (state, action: PayloadAction<boolean>) => {
            state.hasSeenUpsellModal = action.payload;
        },
        setHeaderServiceFailure: (state, action: PayloadAction<boolean>) => {
            state.isHeaderServiceFailure = action.payload;
            setKey("isHeaderServiceFailure", JSON.stringify(action.payload));
        },
        setSnackBarOptions: (state, action: PayloadAction<SnackBarOptions>) => {
            state.snackBarOptions = {...state.snackBarOptions, ...action.payload};
        },
        setResetPasswordSuccessMsg: (state, action: PayloadAction<string>) => {
            state.resetPasswordSuccessMsg = action.payload;
        },
        setForgotPasswordMsg: (state, action: PayloadAction<string>) => {
            state.forgotPasswordMsg = action.payload;
        },
        updateSignUpForm: (state, action: PayloadAction<ISignUpForm>) => {
            state.signUpForm = action.payload;
        },
        setSignUpFormValidation: (state, action: PayloadAction<boolean>) => {
            state.isSignUpFormInvalid = action.payload;
        },
        setWelcomeModalOpen: (state, action: PayloadAction<boolean>) => {
            state.welcomeModalOpen = action.payload;
        },
        setPreferencesModalOpen: (state, action: PayloadAction<boolean>) => {
            state.preferencesModalOpen = action.payload;
        },
        setAlreadySignUpErrorMsg: (state, action: PayloadAction<string | null>) => {
            state.alreadySignUpErrorMsg = action.payload;
        },
        updateChangePasswordForm: (state, action: PayloadAction<ChangePasswordForm>) => {
            state.changePasswordForm = action.payload;
        },
        setChangePasswordFormValidation: (state, action: PayloadAction<boolean>) => {
            state.isChangePasswordFormInvalid = action.payload;
        },
        setConfigs: (state, action: PayloadAction<ConfigResponse>) => {
            state.configs = action.payload;
        },
        setRedirectToAccountNotifications: (state, action: PayloadAction<boolean>) => {
            state.redirectToAccountNotifications = action.payload;
        },
        setRedirectToMyAccount: (state, action: PayloadAction<boolean>) => {
            state.redirectToMyAccount = action.payload;
        },
        setRedirectToGiftCardTransfer: (state, action: PayloadAction<boolean>) => {
            state.redirectToGiftCardTransfer = action.payload;
        },
        setReirectToRewardsHome: (state, action: PayloadAction<boolean>) => {
            state.redirectToRewardsHome = action.payload;
        },
        setShowGlobalAlert: (state, action: PayloadAction<boolean>) => {
            state.showGlobalAlert = action.payload;
        },
        setSelectedMyAccountSelection: (state, action: PayloadAction<string>) => {
            state.selectedMyAccountSelection = action.payload;
        },
        setLogoutRequested: (state, action: PayloadAction<boolean>) => {
            state.logoutRequested = action.payload;
        },
        setForceLogoutErrorMsg: (state, action: PayloadAction<string | null>) => {
            state.forceLogoutErrorMsg = action.payload;
        },
    },
    extraReducers: () => {
    },
});

export const {
    setHideNavigation,
    setCredentials,
    reSetCredentials,
    toggleAuthModal,
    toggleCartDrawer,
    toggleUpsellModal,
    showMobileFooter,
    toggleMoreOptionsDrawer,
    setHasSeenUpsellModal,
    setHeaderServiceFailure,
    setResetPasswordSuccessMsg,
    setForgotPasswordMsg,
    updateSignUpForm,
    setSignUpFormValidation,
    setWelcomeModalOpen,
    setPreferencesModalOpen,
    setConfigs,
    setSnackBarOptions,
    setAlreadySignUpErrorMsg,
    updateChangePasswordForm,
    setChangePasswordFormValidation,
    resetPasswordFields,
    setRedirectToMyAccount,
    setRedirectToGiftCardTransfer,
    setReirectToRewardsHome,
    setRedirectToAccountNotifications,
    setShowGlobalAlert,
    setSelectedMyAccountSelection,
    setLogoutRequested,
    setForceLogoutErrorMsg,
} = authSlice.actions;

export default authSlice.reducer;

export const selectCurrentUser = (state: RootState) => state.auth.user;
